import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { ClipboardList, Star, Send, Loader2, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SurveyFillPanel = () => {
  const { t } = useTranslation();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchSurveys();
  }, []);

  const fetchSurveys = async () => {
    try {
      const response = await axios.get(`${API}/user/surveys`);
      setSurveys(response.data.surveys || []);
    } catch (error) {
      console.error('Error fetching surveys:', error);
    }
    setLoading(false);
  };

  const openSurvey = (survey) => {
    setSelectedSurvey(survey);
    setAnswers({});
  };

  const setRating = (questionIndex, rating) => {
    setAnswers({
      ...answers,
      [questionIndex]: { type: 'rating', value: rating }
    });
  };

  const setText = (questionIndex, text) => {
    setAnswers({
      ...answers,
      [questionIndex]: { type: 'text', value: text }
    });
  };

  const handleSubmit = async () => {
    if (!selectedSurvey) return;
    
    // Check all questions answered
    const requiredCount = selectedSurvey.questions?.length || 0;
    if (Object.keys(answers).length < requiredCount) {
      toast.error(t('answerAllQuestions') || 'Please answer all questions');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/user/surveys/${selectedSurvey.id}/respond`, { answers });
      toast.success(t('surveySubmitted') || 'Survey submitted successfully');
      setSelectedSurvey(null);
      setAnswers({});
      fetchSurveys();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error'));
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  const pendingSurveys = surveys.filter(s => !s.already_responded);

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <ClipboardList className="h-6 w-6 text-indigo-600" />
        <h2 className="text-2xl font-bold">{t('surveys') || 'Surveys'}</h2>
      </div>

      {pendingSurveys.length === 0 ? (
        <Card className="border-0 shadow-md">
          <CardContent className="py-8 text-center text-gray-500">
            <CheckCircle className="h-12 w-12 mx-auto mb-4 text-green-500" />
            <p>{t('noSurveysAvailable') || 'No surveys available at this time'}</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {pendingSurveys.map((survey) => (
            <Card key={survey.id} className="border-0 shadow-md hover:shadow-lg transition-shadow">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{survey.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600 mb-4">{survey.description}</p>
                <p className="text-xs text-gray-400 mb-4">
                  {survey.questions?.length || 0} {t('questions') || 'questions'}
                </p>
                <Button
                  onClick={() => openSurvey(survey)}
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600"
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  {t('fillSurvey') || 'Fill Survey'}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Completed Surveys */}
      {surveys.filter(s => s.already_responded).length > 0 && (
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-600 mb-4">
            {t('completedSurveys') || 'Completed Surveys'}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {surveys.filter(s => s.already_responded).map((survey) => (
              <Card key={survey.id} className="border-0 shadow-md bg-gray-50">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{survey.title}</span>
                    <span className="text-xs text-green-600 flex items-center gap-1">
                      <CheckCircle className="h-4 w-4" />
                      {t('completed') || 'Completed'}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Survey Fill Dialog */}
      <Dialog open={!!selectedSurvey} onOpenChange={() => setSelectedSurvey(null)}>
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedSurvey?.title}</DialogTitle>
          </DialogHeader>
          
          {selectedSurvey?.description && (
            <p className="text-sm text-gray-600">{selectedSurvey.description}</p>
          )}

          <div className="space-y-6 py-4">
            {selectedSurvey?.questions?.map((question, index) => (
              <div key={index} className="space-y-3">
                <label className="font-medium text-sm">
                  {index + 1}. {question.text}
                </label>
                
                {question.type === 'rating' ? (
                  <div className="flex gap-2 justify-center">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setRating(index, star)}
                        className="p-2 hover:scale-110 transition-transform"
                      >
                        <Star
                          className={`h-8 w-8 ${
                            answers[index]?.value >= star
                              ? 'fill-yellow-400 text-yellow-400'
                              : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                  </div>
                ) : question.type === 'choice' ? (
                  <div className="flex gap-4 justify-center">
                    {['Yes', 'No'].map((choice) => (
                      <Button
                        key={choice}
                        variant={answers[index]?.value === choice ? 'default' : 'outline'}
                        onClick={() => setText(index, choice)}
                        className={answers[index]?.value === choice ? 'bg-indigo-600' : ''}
                      >
                        {choice === 'Yes' ? t('yes') || 'Yes' : t('no') || 'No'}
                      </Button>
                    ))}
                  </div>
                ) : (
                  <Textarea
                    placeholder={t('yourAnswer') || 'Your answer...'}
                    value={answers[index]?.value || ''}
                    onChange={(e) => setText(index, e.target.value)}
                    rows={3}
                  />
                )}
              </div>
            ))}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedSurvey(null)}>
              {t('cancel') || 'Cancel'}
            </Button>
            <Button onClick={handleSubmit} disabled={submitting} className="bg-indigo-600">
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Send className="h-4 w-4 mr-2" />
              )}
              {t('submit') || 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SurveyFillPanel;
